<template>
  <div class="recipient-form">
    <div class="heading-wrapper">
      <h2
        class="text-body-2xs-regular"
        v-text="$string('product.recipient_form.title')"
      />
      <span
        class="text-body-2xs-regular"
        v-text="$string('product.recipient_form.optional')"
      />
    </div>

    <div
      class="recipient-form__container form-group"
    >
      <div class="form-group__field floating-label">
        <input
          :id="`gift-card-recipient-name-${section.id}`"
          v-model="local.fields['Recipient\'s first name']"
          class="form-group__input text-body-2xs-regular"
          name="properties[Recipient's first name]"
          type="text"
          placeholder=" "
        >
        <label
          class="form-group__label"
          :for="`gift-card-recipient-name-${section.id}`"
        >
          <span
            class="text-body-2xs-regular"
            v-text="$string('product.recipient_form.first_name.label')"
          />

          <abbr
            :title="$string('product.recipient_form.required.label')"
            v-text="$string('product.recipient_form.required.symbol')"
          />
        </label>
      </div>
      <div class="form-group__field floating-label">
        <input
          :id="`gift-card-recipient-name-${section.id}`"
          v-model="local.fields['Last name of recipient']"
          class="form-group__input text-body-2xs-regular"
          name="properties[Last name of recipient]"
          type="text"
          placeholder=" "
        >
        <label
          class="form-group__label"
          :for="`gift-card-recipient-name-${section.id}`"
        >
          <span
            class="text-body-2xs-regular"
            v-text="$string('product.recipient_form.last_name.label')"
          />

          <abbr
            :title="$string('product.recipient_form.required.label')"
            v-text="$string('product.recipient_form.required.symbol')"
          />
        </label>
      </div>

      <div class="form-group__field floating-label">
        <input
          :id="`gift-card-recipient-email-${section.id}`"
          v-model="local.fields['Recipient Email']"
          class="form-group__input text-body-2xs-regular"
          name="properties[Recipient Email]"
          type="email"
          placeholder=" "
        >
        <label
          class="form-group__label"
          :for="`gift-card-recipient-email-${section.id}`"
        >
          <span
            class="text-body-2xs-regular"
            v-text="$string('product.recipient_form.email.label')"
          />

          <abbr
            :title="$string('product.recipient_form.required.label')"
            v-text="$string('product.recipient_form.required.symbol')"
          />
        </label>
      </div>

      <div class="form-group__field floating-label">
        <textarea
          :id="`gift-card-recipient-message-${section.id}`"
          v-model="local.fields.recipientMessage"
          placeholder=" "
          class="form-group__input text-body-2xs-regular"
          :maxlength="messageLimit"
          name="properties[recipient-message]"
          type="text"
        />

        <label
          class="form-group__label"
          :for="`gift-card-recipient-message-${section.id}`"
        >
          <span
            class="text-body-2xs-regular"
            v-text="$string('product.recipient_form.message.label')"
          />
        </label>

        <span
          class="form-group__footer text-body-2xs-regular"
          v-text="$string(
            'product.recipient_form.message.limit',
            {
              pluralise: messageRemaining,
              replace: { remaining: messageRemaining },
            },
          )"
        />
      </div>
    </div>

    <input
      :id="`gift-card-recipient-control-${section.id}`"
      disabled
      :name="controlFlag"
      type="hidden"
      value="on"
    >

    <input
      :id="`gift-card-recipient-timezone-offset-${section.id}`"
      disabled
      name="properties[__shopify_offset]"
      type="hidden"
      :value="local.timezoneOffset"
    >
  </div>
</template>

<script>

/**
 * Vue: Recipient form (product-form)
 * -----------------------------------------------------------------------------
 * Recipient form for gift card products.
 *
 * @param {Object} giftCard - Gift card props.
 * @param {Object} section - Main product section settings.
 *
 */

export default {
  name: 'RecipientForm',

  props: {
    giftCard: {
      type: Object,
      default: () => ({
        fields: {
          recipientMessage: '',
          'Recipient Email': '',
          'Recipient\'s first name': '',
          'Last name of recipient': '',
        },
        timezoneOffset: '',
      }),
    },
    section: {
      type: Object,
      default: () => ({
        displayRecipientForm: true,
        id: '',
      }),
      required: true,
    },
  },

  emits: {
    'update-recipient-form': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      controlFlag: 'properties[__shopify_send_gift_card_to_recipient]',
      local: {
        fields: {
          recipientMessage: '',
          'Recipient Email': '',
          'Recipient\'s first name': '',
          'Last name of recipient': '',
          'Send on': '',
        },
        timezoneOffset: '',
      },
      messageLimit: 200,
    }
  },

  computed: {

    /**
     * Compute characters remaining in message field.
     * @returns {Number}
     */
    messageRemaining() {
      return this.messageLimit - this.local.fields.recipientMessage.length
    },
  },

  watch: {

    /**
     * Watch gift card fields to update locale field values.
     */
    giftCard: {
      handler() {
        this.updateLocalValues()
      },
      deep: true,
    },

    /**
     * Watch local fields to emit update.
     */
    local: {
      handler() {
        this.$emit('update-recipient-form', this.local)
      },
      deep: true,
    },
  },

  mounted() {
    this.updateLocalValues()
  },

  methods: {

    /**
     * Update local values if they differ.
     */
    updateLocalValues() {
      Object.keys(this.giftCard).forEach((key) => {
        if (this.giftCard[key] === this.local[key]) {
          return
        }

        this.local[key] = this.giftCard[key]
      })
    },
  },
}
</script>

<style lang="scss">
@import './recipient-form';
</style>
